import React, { useMemo, useState } from 'react';
import VuiBox from "components/ui/VuiBox";
import VuiTypography from "components/ui/VuiTypography"; // Restored for main title
import ReactApexChart from "react-apexcharts"; // Use ReactApexChart directly
import ChartInfoTooltip from "components/Tooltip/ChartInfoTooltip";
import StandardLoadingIndicator from "components/StandardLoadingIndicator";
import StandardMessageDisplay from "components/StandardMessageDisplay";
import { getProductMentionVolumeChartOptions } from "../data/productChartOptions";
import EntitySelector from "components/EntitySelector"; // Corrected import path
import GlassCard from "components/GlassCard";

// Helper to calculate total mentions for sorting
const calculateTotalMentions = (dataPoints = []) => {
  return dataPoints.reduce((sum, point) => sum + (point.mentions || 0), 0);
};

const displayFilterOptions = [
  { id: 3, name: "Top 3 Products" },
  { id: 5, name: "Top 5 Products" },
  { id: 10, name: "Top 10 Products" },
  { id: 'all', name: "All Products" },
];

function ProductMentionVolumeChart({ trendsData, loading, error, competitorName }) {
  const [selectedFilterId, setSelectedFilterId] = useState(3); 

  const handleFilterChange = (newFilterId) => {
    setSelectedFilterId(newFilterId);
  };

  const topNProductData = useMemo(() => {
    if (!trendsData || Object.keys(trendsData).length === 0) return {};

    let productsWithTotalMentions = Object.entries(trendsData).map(([productName, dataPoints]) => ({
      name: productName,
      dataPoints: dataPoints,
      totalMentions: calculateTotalMentions(dataPoints),
    }));

    productsWithTotalMentions.sort((a, b) => b.totalMentions - a.totalMentions);

    const topProductsSlice = selectedFilterId === 'all' 
      ? productsWithTotalMentions
      : productsWithTotalMentions.slice(0, selectedFilterId);
    
    const result = {};
    topProductsSlice.forEach(p => {
      result[p.name] = p.dataPoints;
    });
    return result;
  }, [trendsData, selectedFilterId]);

  const allCategories = useMemo(() => {
    if (!topNProductData || Object.keys(topNProductData).length === 0) {
      return [];
    }

    // Collect all dates with at least one mention
    const datesWithMentions = new Set();

    Object.values(topNProductData).forEach(dataPoints => {
      dataPoints.forEach(point => {
        if (point && point.date && point.mentions > 0) {
          datesWithMentions.add(point.date); // Use the date string directly
        }
      });
    });

    // Convert to array and sort chronologically
    return Array.from(datesWithMentions).sort((a, b) => {
      return new Date(a) - new Date(b);
    });
  }, [topNProductData]);

  const chartData = useMemo(() => {
    if (!topNProductData || Object.keys(topNProductData).length === 0 || allCategories.length === 0) return [];

    return Object.entries(topNProductData).map(([productName, dataPoints]) => {
      // Create a map of mentions for the current product: date -> mentions
      const productDataMap = new Map();
      dataPoints.forEach(point => {
        if (point && point.date) {
          productDataMap.set(point.date, point.mentions);
        }
      });

      // For each category (date) in the chart, get the mention count or default to 0
      const seriesData = allCategories.map(date =>
        productDataMap.get(date) || 0
      );

      return {
        name: productName,
        data: seriesData,
      };
    });
  }, [topNProductData, allCategories]);

  const chartOptions = useMemo(() => {
    return getProductMentionVolumeChartOptions(allCategories);
  }, [allCategories]);

  const renderContent = () => {
    if (loading) return <StandardLoadingIndicator containerHeight="350px" />;
    if (error) return <StandardMessageDisplay type="error" containerHeight="350px" />;

    if (!chartData || chartData.length === 0) {
      const filterOption = displayFilterOptions.find(opt => opt.id === selectedFilterId);
      const filterLabel = filterOption ? filterOption.name.toLowerCase() : `${selectedFilterId} products`;
      const message = `No product mention data found for ${selectedFilterId === 'all' ? 'all products' : filterLabel} for ${competitorName} in the selected timeframe, or all selected products have zero mentions.`;
      return <StandardMessageDisplay type="noData" title="No Product Mention Data" message={message} containerHeight="350px" />;
    }

    return (
      <VuiBox sx={{ height: "350px" }}>
        <ReactApexChart 
          options={chartOptions} 
          series={chartData} 
          type={chartOptions.chart.type} 
          width="100%" 
          height="100%" 
        />
      </VuiBox>
    );
  };
  
  const dynamicChartTitle = useMemo(() => {
    const N = selectedFilterId === 'all' ? 'All' : `Top ${selectedFilterId}`;
    return `${N} Advertised ${competitorName ? competitorName : ''} Products`;
  }, [selectedFilterId, competitorName]);

  return (
    <GlassCard>
      <VuiBox sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header Box */}
        <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={1} px={2} pt={1}>
          {/* Title and Info Icon Group */}
          <VuiBox display="flex" alignItems="center">
            <VuiTypography variant="h6" fontWeight="bold" color="white" sx={{ mr: 0.5 /* Spacing between title and icon */ }}>
              {dynamicChartTitle}
            </VuiTypography>
            <ChartInfoTooltip 
              tooltipText={"Displays the trend of how heavily are competitors advertising specific products. How to use: Track product visibility, identify impacts of marketing campaigns or events on engagement, and compare product buzz over time."}
              // No title prop, no children needed if icon renders standalone based on tooltipText
            />
          </VuiBox>
          
          {/* Entity Selector */}
          <EntitySelector
            entities={displayFilterOptions}
            selectedEntityId={selectedFilterId}
            onEntityChange={handleFilterChange}
            placeholder="Select Filter"
            icon="filter_list" 
            width="200px" 
          />
        </VuiBox>

        {/* Chart Content Area - ensure it takes remaining space if needed */}
        <VuiBox sx={{ flexGrow: 1, px: 2, pb: 2 }}> {/* Added px and pb for padding around chart */}
          {renderContent()}
        </VuiBox>
      </VuiBox>
    </GlassCard>
  );
}

export default ProductMentionVolumeChart; 